import { useCallback, useState } from "react";
import {
  useAuth,
  useTwilioActionsQuery,
  useTwilioBuyTFNMutation,
  useTwilioLoadTFNEmbedQuery,
  useTwilioSearchTFNQuery,
  type TwilioPhoneNumber,
} from "@tellescope/tanstack-query";
import { useStateWithSetAction } from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";

/**
 * Base URL of the Twilio-hosted compliance onboarding embed. This host is environment-specific — confirm
 * the correct value for the deployment before going to production. Only `token` and `inquiryId` are dynamic.
 * See docs/tfn-verification-embed.md.
 */
const TWILIO_EMBED_BASE = "https://compliance-embed-poc-2156-dev.twil.io/index.html";

export const useTwilioPhoneNumbers = () => {
  const { isAdmin } = useAuth();
  const [areaCodeQuery, setAreaCodeQuery] = useState("");
  const [debouncedAreaCodeQuery] = useDebouncedValue(areaCodeQuery);
  const [digitsQuery, setDigitsQuery] = useState("");
  const [debouncedDigitsQuery] = useDebouncedValue(digitsQuery);
  const [isBuying, setIsBuying] = useStateWithSetAction(false, (isBuying) => {
    if (!isBuying) {
      setAreaCodeQuery("");
      setDigitsQuery("");
    }
  });
  const [verifyingNumber, setVerifyingNumber] = useState<TwilioPhoneNumber | null>(null);

  const actionsQuery = useTwilioActionsQuery();
  const { data: actionsData, isLoading: actionsIsLoading } = actionsQuery;
  const searchTFNQuery = useTwilioSearchTFNQuery({
    request: {
      areaCode: debouncedAreaCodeQuery,
      contains: debouncedDigitsQuery,
    },
    enabled: isBuying,
  });
  const searchedNumbers = searchTFNQuery.data?.data ?? [];
  const isSearching = searchTFNQuery.isPending;
  const purchasedNumbers = actionsData?.data.phoneNumbers ?? [];

  const cannotBuy = !actionsData?.data.availableActions.includes("Buy TFN");
  const isLoading = actionsIsLoading;
  const buyTFNMutation = useTwilioBuyTFNMutation();
  const buyTFN = useCallback(
    async (...request: Parameters<typeof buyTFNMutation.mutateAsync>) => {
      await buyTFNMutation.mutateAsync(...request);
    },
    [buyTFNMutation]
  );

  const embedQuery = useTwilioLoadTFNEmbedQuery({ phoneNumberSid: verifyingNumber?.sid });
  const embed = embedQuery.data;
  const verificationEmbedUrl =
    embed?.inquiryId && embed.token
      ? `${TWILIO_EMBED_BASE}?token=${encodeURIComponent(embed.token)}&inquiryId=${encodeURIComponent(embed.inquiryId)}`
      : undefined;

  const startVerification = useCallback((number: TwilioPhoneNumber) => setVerifyingNumber(number), []);
  const closeVerification = useCallback(() => {
    setVerifyingNumber(null);
    // The status transitions after the user submits inside the embed — refetch to reflect it.
    void actionsQuery.refetch();
  }, [actionsQuery]);

  return {
    isAdmin,
    areaCodeQuery,
    setAreaCodeQuery,
    digitsQuery,
    setDigitsQuery,
    isBuying,
    setIsBuying,
    searchTFNQuery,
    searchedNumbers,
    isSearching,
    purchasedNumbers,
    cannotBuy,
    isLoading,
    actionsIsLoading,
    buyTFN,
    isPurchasing: buyTFNMutation.isPending,
    verifyingNumber,
    startVerification,
    closeVerification,
    verificationEmbedUrl,
    isLoadingEmbed: embedQuery.isFetching,
    embedError: embedQuery.isError,
  };
};
